import { type FleetFuelIntegration, NewFuelCardRequester } from '@cobliteam/api-lib';
import { Environment } from '@cobliteam/react-environments';
import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

// Hierarchical on purpose, like `CARD_PROVIDERS_QUERY_KEY`: an invalidation of the `fuelCard`
// domain has to see this key too.
export const FLEET_FUEL_INTEGRATIONS_QUERY_KEY = ['fuelCard', 'fleetIntegrations'] as const;

export type UseFleetFuelIntegrationsOptions = Pick<UseQueryOptions<FleetFuelIntegration[]>, 'refetchOnMount'>;

/**
 * The read behind `E1`/`T1` (FND-07). The fuel card route mounts it on load: an active
 * integration is what opens the management page instead of the landing page, and the requested
 * ones feed the landing page. The Integrations catalog also mounts it on load, for the status
 * badges on the fuel card partners.
 *
 * The global `QueryClient` sets `refetchOnMount: false` and a five-minute `staleTime`, so a
 * caller mounted after the route reads the route's cache. The dialog that owns E1 passes
 * `refetchOnMount: 'always'`: its duplicate checks decide on this list being current, and it
 * gates on `isFetching`, so the refetch shows as E1. The option is only forwarded when given — an
 * explicit `undefined` would override the global default that exists to stop refetch loops.
 *
 * `retry: false` is part of the fail-closed contract on `telas/00-E1.jpg`: "Tentar novamente volta
 * ao estado E1 e refaz a chamada" is a user action, not a silent background retry that would delay
 * reaching E2 by several seconds behind React Query's default backoff.
 *
 * `refetchOnReconnect: false` for the same reason `refetchOnWindowFocus` is off: a background
 * refetch triggered by the network coming back must not flip `isFetching`/`isError` under a
 * customer who is already past `T1`, filling out `T2`.
 */
const useFleetFuelIntegrations = ({ refetchOnMount }: UseFleetFuelIntegrationsOptions = {}) => {
  return useQuery<FleetFuelIntegration[]>({
    queryKey: [...FLEET_FUEL_INTEGRATIONS_QUERY_KEY],
    queryFn: () => new NewFuelCardRequester({ host: Environment.getInstance().getEnvironment().apiHost }).getFleetIntegrations(),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...(refetchOnMount !== undefined && { refetchOnMount }),
  });
};

export default useFleetFuelIntegrations;
